import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  buildCountryOptions,
  CameraLocationSearchResult,
  searchCameraLocations,
} from "./locationSearch";
import InteractiveCameraLocationMap from "./InteractiveCameraLocationMap";
import "./CameraLocationTab.css";

type CameraLocation = {
  latitude: number;
  longitude: number;
  label?: string;
};

type CameraLike = {
  cameraId: string;
  name?: string;
  location?: CameraLocation | string | null;
  [key: string]: any;
};

type Props = {
  camera: CameraLike;
  onUpdateCamera: (camera: any) => void;
};

const CAMERAS_API =
  "https://f0usr04663.execute-api.us-east-1.amazonaws.com/cameras";

function coordinatesFromLocation(location: CameraLike["location"]) {
  if (!location || typeof location !== "object") {
    return { latitude: "", longitude: "" };
  }

  return {
    latitude: Number.isFinite(Number(location.latitude))
      ? String(location.latitude)
      : "",
    longitude: Number.isFinite(Number(location.longitude))
      ? String(location.longitude)
      : "",
  };
}

function labelFromLocation(location: CameraLike["location"]) {
  if (typeof location === "string") return location;
  return location?.label || "";
}

function validLatitude(value: number) {
  return Number.isFinite(value) && value >= -90 && value <= 90;
}

function validLongitude(value: number) {
  return Number.isFinite(value) && value >= -180 && value <= 180;
}

async function getIdTokenOrThrow() {
  const session = await fetchAuthSession();
  if (!session.tokens?.idToken) {
    throw new Error("No ID token found. Please sign out and sign in again.");
  }
  return session.tokens.idToken.toString();
}

async function readResponse(res: Response) {
  const text = await res.text();
  try {
    return { text, json: JSON.parse(text) };
  } catch {
    return { text, json: null };
  }
}

export default function CameraLocationTab({ camera, onUpdateCamera }: Props) {
  const initialCoordinates = coordinatesFromLocation(camera.location);
  const [latitude, setLatitude] = useState(initialCoordinates.latitude);
  const [longitude, setLongitude] = useState(initialCoordinates.longitude);
  const [label, setLabel] = useState(labelFromLocation(camera.location));
  const [countryCode, setCountryCode] = useState("");
  const [searchResults, setSearchResults] = useState<CameraLocationSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [status, setStatus] = useState("");
  const [isError, setIsError] = useState(false);
  const searchAbortRef = useRef<AbortController | null>(null);
  const countryOptions = useMemo(() => buildCountryOptions(), []);

  useEffect(() => {
    const coordinates = coordinatesFromLocation(camera.location);
    setLatitude(coordinates.latitude);
    setLongitude(coordinates.longitude);
    setLabel(labelFromLocation(camera.location));
    setCountryCode("");
    setSearchResults([]);
    setStatus("");
    setIsError(false);
    searchAbortRef.current?.abort();
    return () => searchAbortRef.current?.abort();
  }, [camera.cameraId, camera.location]);

  const parsedLatitude = Number(latitude);
  const parsedLongitude = Number(longitude);
  const hasCoordinates =
    latitude.trim() !== "" &&
    longitude.trim() !== "" &&
    validLatitude(parsedLatitude) &&
    validLongitude(parsedLongitude);

  function validateLocation() {
    if (!hasCoordinates) {
      throw new Error(
        "Choose a location from the search results, use your current location, or enter exact coordinates.",
      );
    }
    if (label.trim().length > 160) {
      throw new Error("Location name must be 160 characters or fewer.");
    }
  }

  async function persistLocation(location: CameraLocation | null) {
    const idToken = await getIdTokenOrThrow();
    const response = await fetch(
      `${CAMERAS_API}/${encodeURIComponent(camera.cameraId)}`,
      {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ location }),
      },
    );

    const { text, json } = await readResponse(response);
    if (!response.ok) {
      throw new Error(
        json?.message || text || response.statusText || "Unable to save location.",
      );
    }

    const updatedCamera =
      json?.camera || {
        ...camera,
        location,
        updatedAt: new Date().toISOString(),
      };
    onUpdateCamera(updatedCamera);
    return updatedCamera;
  }

  async function findLocation() {
    const query = label.trim();
    if (query.length < 2) {
      setIsError(true);
      setStatus("Type at least two characters before searching for a location.");
      return;
    }

    searchAbortRef.current?.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;
    setSearching(true);
    setIsError(false);
    setStatus("Searching for matching locations…");
    setSearchResults([]);

    try {
      const results = await searchCameraLocations({
        query,
        countryCode,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setSearchResults(results);
      setStatus(
        results.length
          ? "Choose the correct location below. The exact coordinates will be filled automatically."
          : "No matching locations were found. Try a broader name/address or another country.",
      );
      setIsError(!results.length);
    } catch (error) {
      if (controller.signal.aborted) return;
      setIsError(true);
      setStatus(
        error instanceof Error
          ? error.message
          : "Location search is temporarily unavailable.",
      );
    } finally {
      if (searchAbortRef.current === controller) searchAbortRef.current = null;
      setSearching(false);
    }
  }

  function chooseSearchResult(result: CameraLocationSearchResult) {
    setLatitude(result.latitude.toFixed(7));
    setLongitude(result.longitude.toFixed(7));
    setLabel(result.displayName.slice(0, 160));
    if (result.countryCode) setCountryCode(result.countryCode);
    setSearchResults([]);
    setIsError(false);
    setStatus("Location selected. Verify the map pin, then save.");
  }

  async function saveLocation() {
    setSaving(true);
    setStatus("Saving exact camera location…");
    setIsError(false);

    try {
      validateLocation();
      const normalizedLabel = label.trim();
      await persistLocation({
        latitude: parsedLatitude,
        longitude: parsedLongitude,
        ...(normalizedLabel ? { label: normalizedLabel } : {}),
      });
      setSearchResults([]);
      setStatus("Camera location saved successfully.");
    } catch (error) {
      setIsError(true);
      setStatus(
        error instanceof Error ? error.message : "Unable to save camera location.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function removeLocation() {
    if (!window.confirm("Remove the saved location for this camera?")) return;

    setSaving(true);
    setStatus("Removing camera location…");
    setIsError(false);
    try {
      await persistLocation(null);
      setLatitude("");
      setLongitude("");
      setLabel("");
      setCountryCode("");
      setSearchResults([]);
      setStatus("Camera location removed.");
    } catch (error) {
      setIsError(true);
      setStatus(
        error instanceof Error ? error.message : "Unable to remove camera location.",
      );
    } finally {
      setSaving(false);
    }
  }

  function useCurrentLocation() {
    if (!navigator.geolocation) {
      setIsError(true);
      setStatus("This browser does not support location access.");
      return;
    }

    setLocating(true);
    setIsError(false);
    setSearchResults([]);
    setStatus("Reading this device’s current coordinates…");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude.toFixed(7));
        setLongitude(position.coords.longitude.toFixed(7));
        setLocating(false);
        const accuracy = Number.isFinite(position.coords.accuracy)
          ? ` Estimated device accuracy: ±${Math.round(position.coords.accuracy)} m.`
          : "";
        setStatus(`Coordinates loaded. Verify the pin before saving.${accuracy}`);
      },
      (error) => {
        setLocating(false);
        setIsError(true);
        setStatus(
          error.message ||
            "Unable to read the device location. Search for the address or enter coordinates manually.",
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  }

  const hasSavedLocation = Boolean(
    camera.location &&
      typeof camera.location === "object" &&
      validLatitude(Number(camera.location.latitude)) &&
      validLongitude(Number(camera.location.longitude)),
  );

  return (
    <section className="camera-location-tab">
      <div className="camera-location-heading">
        <div>
          <span className="camera-location-eyebrow">Physical camera position</span>
          <h3>Camera location</h3>
          <p>
            Pick a country and search for the site, building, street, or address.
            Choosing a result fills the exact map coordinates automatically. Then drag
            the pin or click the map to fine-tune the camera position before saving.
          </p>
        </div>
        <span className={`camera-location-state ${hasSavedLocation ? "saved" : ""}`}>
          {hasSavedLocation ? "Location saved" : "Not configured"}
        </span>
      </div>

      <div className="camera-location-layout">
        <div className="camera-location-form-card">
          <label className="camera-location-field camera-location-field-wide">
            <span>Country</span>
            <select
              value={countryCode}
              onChange={(event) => {
                setCountryCode(event.target.value);
                setSearchResults([]);
              }}
              disabled={saving || searching}
            >
              <option value="">All countries</option>
              {countryOptions.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </label>

          <label className="camera-location-field camera-location-field-wide">
            <span>Location name or address</span>
            <div className="camera-location-search-row">
              <input
                value={label}
                maxLength={160}
                onChange={(event) => {
                  setLabel(event.target.value);
                  setSearchResults([]);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void findLocation();
                  }
                }}
                placeholder="e.g. 100 King St W, Toronto or Warehouse A"
                disabled={saving || searching}
              />
              <button
                type="button"
                className="camera-location-search-button"
                onClick={() => void findLocation()}
                disabled={saving || searching || label.trim().length < 2}
              >
                {searching ? "Searching…" : "Search"}
              </button>
            </div>
          </label>

          {searchResults.length > 0 && (
            <div className="camera-location-results" role="listbox" aria-label="Location search results">
              {searchResults.map((result, index) => (
                <button
                  type="button"
                  key={`${result.latitude}-${result.longitude}-${index}`}
                  className="camera-location-result"
                  onClick={() => chooseSearchResult(result)}
                >
                  <span className="camera-location-result-pin">⌖</span>
                  <span className="camera-location-result-copy">
                    <strong>{result.displayName}</strong>
                    <small>
                      {result.countryName || result.countryCode || "Location"} · {result.latitude.toFixed(5)}, {result.longitude.toFixed(5)}
                    </small>
                  </span>
                </button>
              ))}
              <div className="camera-location-search-attribution">
                Search data © OpenStreetMap contributors
              </div>
            </div>
          )}

          <div className="camera-location-quick-actions">
            <button
              type="button"
              className="camera-location-secondary"
              onClick={useCurrentLocation}
              disabled={saving || locating || searching}
            >
              {locating ? "Locating…" : "Use my current location"}
            </button>
            <span>or search above and choose the correct result</span>
          </div>

          <details className="camera-location-advanced">
            <summary>
              <span>Exact coordinates</span>
              <em>Optional to type manually</em>
            </summary>
            <div className="camera-location-coordinate-grid">
              <label className="camera-location-field">
                <span>Latitude</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={-90}
                  max={90}
                  step="any"
                  value={latitude}
                  onChange={(event) => setLatitude(event.target.value)}
                  placeholder="Filled automatically"
                  disabled={saving}
                />
              </label>
              <label className="camera-location-field">
                <span>Longitude</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={-180}
                  max={180}
                  step="any"
                  value={longitude}
                  onChange={(event) => setLongitude(event.target.value)}
                  placeholder="Filled automatically"
                  disabled={saving}
                />
              </label>
            </div>
          </details>

          <div className="camera-location-tip">
            <strong>No latitude/longitude knowledge required</strong>
            <span>
              Search for the location and select a match, or use the device’s current
              location. Qumra fills the map coordinates for you.
            </span>
          </div>

          <div className="camera-location-actions">
            <div className="camera-location-action-spacer" />
            {hasSavedLocation && (
              <button
                type="button"
                className="camera-location-danger"
                onClick={() => void removeLocation()}
                disabled={saving || locating || searching}
              >
                Remove
              </button>
            )}
            <button
              type="button"
              className="camera-location-primary"
              onClick={() => void saveLocation()}
              disabled={saving || locating || searching}
            >
              {saving ? "Saving…" : "Save location"}
            </button>
          </div>

          {status && (
            <div
              className={`camera-location-status ${isError ? "error" : ""}`}
              role={isError ? "alert" : "status"}
            >
              {status}
            </div>
          )}
        </div>

        <div className="camera-location-map-card">
          {hasCoordinates ? (
            <InteractiveCameraLocationMap
              latitude={parsedLatitude}
              longitude={parsedLongitude}
              cameraName={camera.name || camera.cameraId}
              disabled={saving || locating || searching}
              onPositionChange={(nextLatitude, nextLongitude, source) => {
                setLatitude(nextLatitude.toFixed(7));
                setLongitude(nextLongitude.toFixed(7));
                setSearchResults([]);
                setIsError(false);
                setStatus(
                  source === "drag"
                    ? "Pin moved. Verify the exact position, then save."
                    : "Pin placed on the map. Verify the exact position, then save.",
                );
              }}
            />
          ) : (
            <div className="camera-location-map-empty">
              <div className="camera-location-pin">⌖</div>
              <strong>Choose a starting location</strong>
              <span>
                Search for a location, use your current location, or enter coordinates.
                Once the pin appears, drag it or click the map to set the exact camera position.
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
